<template>
  <section class="atlas-ingestion-analyze-panel">
    <div class="atlas-ingestion-analyze-panel__selected-folder">
      <span class="atlas-ingestion-analyze-panel__selected-folder-icon">
        <IconEmbedded name="save_2" :size="22" color="#118689" />
      </span>
      <div class="atlas-ingestion-analyze-panel__selected-folder-text">
        <span class="atlas-ingestion-analyze-panel__selected-folder-label">Selected folder</span>
        <span class="atlas-ingestion-analyze-panel__selected-folder-name">\{{ folderLabel || "Unknown folder" }}</span>
        <span class="atlas-ingestion-analyze-panel__selected-folder-stats">
          <b>{{ imageCount.toLocaleString() }} images found</b>
          <span class="atlas-ingestion-analyze-panel__selected-folder-sep" aria-hidden="true">·</span>
          {{ excludedFileCount.toLocaleString() }} files skipped
        </span>
      </div>
      <SrpButton class="atlas-ingestion-analyze-panel__change-btn" size="small" color="gray" fill="outlined" :isDisabled="isAnalyzing || isUploading" @click="emit('changeFolder')">
        <template #icon><IconEmbedded name="reload_2" :size="16" /></template>
        Change
      </SrpButton>
    </div>

    <h2 class="global-h2">How are your images organized?</h2>
    <div class="atlas-ingestion-analyze-panel__mode-grid" role="radiogroup" aria-label="Atlas ingestion mode">
      <button
        v-for="modeOption in ingestionModeOptions"
        :key="modeOption.value"
        type="button"
        class="atlas-ingestion-analyze-panel__mode-option"
        :class="{ 'atlas-ingestion-analyze-panel__mode-option--active': groupingMode === modeOption.value }"
        :aria-pressed="groupingMode === modeOption.value"
        @click="onModeClick(modeOption.value)"
      >
        <span class="atlas-ingestion-analyze-panel__mode-indicator" :class="{ 'atlas-ingestion-analyze-panel__mode-indicator--active': groupingMode === modeOption.value }">
          <span class="atlas-ingestion-analyze-panel__mode-indicator-dot" />
        </span>
        <span class="atlas-ingestion-analyze-panel__mode-copy">
          <span class="atlas-ingestion-analyze-panel__mode-title">{{ modeOption.label }}</span>
          <span class="atlas-ingestion-analyze-panel__small-note">{{ modeOption.description }}</span>
        </span>
      </button>
    </div>
    <template v-if="groupingMode !== 'singleGroup'">
      <label class="atlas-ingestion-analyze-panel__field-label atlas-ingestion-analyze-panel__guidance-label" for="atlas-ingestion-guidance">
        Any guidance about your folder structure? (optional)
      </label>
      <textarea
        id="atlas-ingestion-guidance"
        :value="groupingGuidance"
        rows="3"
        class="atlas-ingestion-analyze-panel__textarea"
        placeholder="Ex: The top level folders are all businesses."
        style="height: 70px"
        @input="emit('update:groupingGuidance', ($event.target as HTMLTextAreaElement).value)"
        @blur="emit('persist')"
      />
    </template>

    <div v-if="groupingMode === 'singleGroup'" class="atlas-ingestion-analyze-panel__single-group-name-wrap">
      <label class="atlas-ingestion-analyze-panel__field-label" for="atlas-single-group-name">Single group name (optional)</label>
      <input
        id="atlas-single-group-name"
        :value="singleGroupName"
        class="atlas-ingestion-analyze-panel__group-name-input"
        @input="emit('update:singleGroupName', ($event.target as HTMLInputElement).value)"
        @blur="emit('persist')"
      />
    </div>
    <div class="atlas-ingestion-analyze-panel__actions">
      <NoteWithIcon v-if="limitWarningMessage" color="yellow" icon="warning_2" role="status">
        <template #text>
          <p v-for="(line, i) in limitWarningMessage" :key="i" class="global-p">
            {{ line }}
          </p>
        </template>
      </NoteWithIcon>
      <NoteWithIcon v-else-if="limitErrorMessage" color="red" icon="warning_2" role="alert">
        <template #text>
          <p v-for="(line, i) in limitErrorMessage" :key="i" class="global-p">
            {{ line }}
          </p>
        </template>
      </NoteWithIcon>
      <NoteWithIcon v-else color="green" role="status">
        <template #icon>
          <IconEmbedded name="ai-triple-solid" :size="20" />
        </template>
        <template #text>
          <p class="global-p">
            We'll analyze <strong>{{ imageCount.toLocaleString() }} images</strong> and propose upload groups.
          </p>
          <p class="global-p">This typically takes 30-60 seconds.</p>
        </template>
      </NoteWithIcon>
      <div class="atlas-ingestion-analyze-panel__analyze-info-divider" aria-hidden="true" />
      <div class="atlas-ingestion-analyze-panel__action-button-row">
        <SrpButton :isDisabled="!canAnalyze" @click="emit('analyze')">
          <template #icon>
            <LoadingIcon v-if="isAnalyzing" :size="19" />
            <IconEmbedded v-else name="ai-triple-solid" :size="19" />
          </template>
          {{ isAnalyzing ? "Analyzing..." : "Analyze Groups" }}
        </SrpButton>
        <SrpButton class="atlas-ingestion-analyze-panel__back-btn" color="gray" fill="outlined" :isDisabled="isAnalyzing || isUploading" @click="emit('restart')">
          <!--<template #icon><IconEmbedded name="arrow-left_2" :size="17" /></template>-->
          Back
        </SrpButton>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import { ContentIngestionGroupingMode } from "@contracts/contentIngestion";

defineOptions({
  name: "AtlasIngestionAnalyzePanel",
});

interface GroupingModeOption {
  value: ContentIngestionGroupingMode;
  label: string;
  description: string;
}

const ingestionModeOptions: Array<GroupingModeOption> = [
  {
    value: "folder",
    label: "Grouped in Folders",
    description: "Folders for businesses, places, or events.",
  },
  {
    value: "jumbled",
    label: "Jumbled Files",
    description: "AI will sort mixed files into groups.",
  },
  {
    value: "singleGroup",
    label: "Single Group",
    description: "Put everything into a one group.",
  },
];

const props = defineProps<{
  folderLabel: string;
  imageCount: number;
  excludedFileCount: number;
  isAnalyzing: boolean;
  isUploading: boolean;
  canAnalyze: boolean;
  /** Shown above the Analyze button when selection exceeds upload cap but is within analyze cap. */
  limitWarningMessage: ReadonlyArray<string> | null;
  /** Shown above the Analyze button when analysis cannot proceed (e.g. folder too large or gallery full). */
  limitErrorMessage: ReadonlyArray<string> | null;
  groupingMode: ContentIngestionGroupingMode;
  groupingGuidance: string;
  singleGroupName: string;
}>();

const emit = defineEmits<{
  "update:groupingMode": [ContentIngestionGroupingMode];
  "update:groupingGuidance": [string];
  "update:singleGroupName": [string];
  changeFolder: [];
  analyze: [];
  restart: [];
  persist: [];
}>();

function onModeClick(mode: ContentIngestionGroupingMode) {
  if (props.groupingMode === mode) {
    return;
  }
  emit("update:groupingMode", mode);
}
</script>

<style scoped lang="scss">
.atlas-ingestion-analyze-panel {
  margin-top: 20px;

  &__selected-folder {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    margin-bottom: 30px;
    border-radius: 10px;
    background: rgb(206, 228, 200);
    // background: rgba(17, 134, 137, 0.12);
  }

  &__selected-folder-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  &__selected-folder-text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__selected-folder-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--srp-color-text-muted, #888);
    text-transform: none;
    letter-spacing: 0.01em;
  }

  &__selected-folder-name {
    font-weight: 700;
    font-size: 16px;
    line-height: 1.3;
    color: #1a1a1a;
    word-break: break-word;
  }

  &__selected-folder-stats {
    margin-top: 2px;
    margin-left: 12px;
    font-size: 14px;
    line-height: 1.4;
    color: var(--srp-color-text-muted, #666);
  }

  &__selected-folder-sep {
    margin: 0 6px;
    color: var(--srp-color-text-muted, #999);
  }

  &__change-btn {
    flex-shrink: 0;
    align-self: center;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
    margin-top: 30px;
  }

  :deep(.note-with-icon__text strong) {
    font-weight: 700;
  }

  &__analyze-info-divider {
    height: 1px;
    background: var(--srp-color-border-subtle, #e0e0e0);
    margin: 0;
  }

  &__action-button-row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
  }

  &__back-btn {
    margin-left: auto;
  }

  &__mode-grid {
    margin-top: 10px;
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  &__mode-option {
    width: 100%;
    text-align: left;
    appearance: none;
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid var(--srp-color-border-subtle, #ddd);
    border-radius: 8px;
    padding: 12px 14px;
    background: #fff;
    cursor: pointer;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease,
      background-color 0.15s ease;
  }

  &__mode-option:hover {
    border-color: var(--srp-color-blue, #2d6cdf);
  }

  &__mode-option--active {
    border-color: var(--srp-color-blue, #2d6cdf);
    box-shadow: 0 0 0 1px rgba(45, 108, 223, 0.18);
    background: rgba(45, 108, 223, 0.04);
  }

  &__mode-indicator {
    width: 18px;
    height: 18px;
    min-width: 18px;
    border-radius: 50%;
    border: 2px solid var(--srp-color-border-subtle, #bbb);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__mode-indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: transparent;
  }

  &__mode-indicator--active {
    border-color: var(--srp-color-blue, #2d6cdf);
  }

  &__mode-indicator--active .atlas-ingestion-analyze-panel__mode-indicator-dot {
    background: var(--srp-color-blue, #2d6cdf);
  }

  &__mode-copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__mode-title {
    font-weight: 600;
    font-size: 16px;
  }

  &__field-label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  &__guidance-label {
    margin-top: 18px !important;
  }

  &__textarea {
    width: 100%;
    max-width: 720px;
    border: 1px solid var(--srp-color-border-subtle, #ccc);
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 14px;
    resize: vertical;
  }

  &__single-group-name-wrap {
    margin-top: 18px;
  }

  &__group-name-input {
    min-width: 280px;
    max-width: 350px;
    border: 1px solid var(--srp-color-border-subtle, #ccc);
    border-radius: 4px;
    padding: 6px 8px;
  }

  &__small-note {
    margin-top: 8px;
    color: var(--srp-color-text-muted, #666);
    font-size: 13px;
  }

  &__mode-copy .atlas-ingestion-analyze-panel__small-note {
    margin-top: 0;
  }
}
</style>
